import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export type ReligionType = 'jewish' | 'christian' | 'muslim' | 'hindu' | 'other';
export type ReligionIntent = 'marriage' | 'serious' | 'exploring';

export interface ReligionSettings {
  id: string;
  user_id: string;
  religion: ReligionType;
  intent: ReligionIntent;
  is_enabled: boolean;
  religion_changed_at: string | null;
  intent_changed_at: string | null;
  wali_note: string | null;
  created_at: string;
  updated_at: string;
}

export function useReligionSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<ReligionSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [isBanned, setIsBanned] = useState(false);
  const [banInfo, setBanInfo] = useState<{ reason: string; expires_at: string | null } | null>(null);

  const fetchSettings = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Check for active ban
      const { data: banData } = await supabase
        .from('religion_bans')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .or('expires_at.is.null,expires_at.gt.now()')
        .single();

      if (banData) {
        setIsBanned(true);
        setBanInfo({ reason: banData.reason, expires_at: banData.expires_at });
      }

      // Fetch settings
      const { data, error } = await supabase
        .from('religion_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching religion settings:', error);
      }

      setSettings(data);
    } catch (error) {
      console.error('Error in fetchSettings:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const selectReligion = async (religion: ReligionType) => {
    if (!user) return { error: new Error('Not authenticated') };

    try {
      // First check if user has a profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (profileError || !profileData) {
        // Create a basic profile first
        const { error: createProfileError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            name: user.email?.split('@')[0] || 'User',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (createProfileError) {
          console.error('Error creating profile:', createProfileError);
          toast.error('Please complete your profile first');
          return { error: createProfileError };
        }
      }

      // Clear intent if switching away from Muslim to another religion
      const shouldClearIntent = settings?.religion === 'muslim' && religion !== 'muslim';

      const { data, error } = await supabase
        .from('religion_settings')
        .upsert({
          user_id: user.id,
          religion,
          intent: shouldClearIntent ? 'exploring' : (settings?.intent || 'exploring'),
          religion_changed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        })
        .select()
        .single();

      if (error) throw error;
      setSettings(data);
      toast.success(`${religion.charAt(0).toUpperCase() + religion.slice(1)} selected`);
      return { error: null };
    } catch (error) {
      console.error('Error selecting religion:', error);
      toast.error('Failed to save your selection. Please try again.');
      return { error };
    }
  };

  const setIntent = async (intent: ReligionIntent) => {
    if (!user) return { error: new Error('Not authenticated') };

    try {
      const { data, error } = await supabase
        .from('religion_settings')
        .update({
          intent,
          intent_changed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      setSettings(data);
      toast.success('Intent updated');
      return { error: null };
    } catch (error) {
      console.error('Error setting intent:', error);
      toast.error('Failed to save intent. Please try again.');
      return { error };
    }
  };

  const updateWaliNote = async (note: string) => {
    if (!user || !settings) return { error: new Error('No settings') };

    try {
      const { data, error } = await supabase
        .from('religion_settings')
        .update({
          wali_note: note,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      setSettings(data);
      return { error: null };
    } catch (error) {
      console.error('Error updating wali note:', error);
      return { error };
    }
  };

  const disableReligionMode = async () => {
    if (!user || !settings) return { error: new Error('No settings') };

    try {
      const { error } = await supabase
        .from('religion_settings')
        .update({
          is_enabled: false,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;
      setSettings(prev => prev ? { ...prev, is_enabled: false } : null);
      return { error: null };
    } catch (error) {
      console.error('Error disabling religion mode:', error);
      return { error };
    }
  };

  return {
    settings,
    loading,
    isBanned,
    banInfo,
    selectReligion,
    setIntent,
    updateWaliNote,
    disableReligionMode,
    refetch: fetchSettings
  };
}
